import api from '@/services/Api';

class Revisions {
  static index() {
    return api.get('/technical-areas/revisions');
  }

  static permissions() {
    return api.get('/technical-areas/revisions/permissions');
  }

  static activeStructure() {
    return api.get('/technical-areas/revisions/active/structure');
  }

  static create(payload) {
    return api.post('/technical-areas/revisions', payload);
  }

  static show(id) {
    return api.get(`/technical-areas/revisions/${id}`);
  }

  static update(id, snapshot) {
    return api.put(`/technical-areas/revisions/${id}`, { snapshot });
  }

  static discard(id) {
    return api.post(`/technical-areas/revisions/${id}/discard`);
  }

  static requestValidation(id) {
    return api.post(`/technical-areas/revisions/${id}/request-validation`);
  }

  static recordVote(id, response) {
    return api.post(`/technical-areas/revisions/${id}/votes`, { response });
  }

  static apply(id) {
    return api.post(`/technical-areas/revisions/${id}/apply`);
  }

  static keywordRemovalImpact(id) {
    return api.get(`/technical-areas/revisions/${id}/keyword-removal-impact`);
  }

  static sponsorshipInheritanceImpact(id) {
    return api.get(`/technical-areas/revisions/${id}/sponsorship-inheritance-impact`);
  }

  static activeKeywords() {
    return api.get('/technical-areas/revisions/active/keywords');
  }

  static updateTechnicalCommitteeKeywords(revisionId, technicalCommitteeId, keywords) {
    return api.put(
      `/technical-areas/revisions/${revisionId}/keywords/technical-committees/${technicalCommitteeId}`,
      { keywords },
    );
  }

  static submitTechnicalCommitteeKeywords(revisionId, technicalCommitteeId) {
    return api.post(
      `/technical-areas/revisions/${revisionId}/keywords/technical-committees/${technicalCommitteeId}/submit`,
    );
  }

  static submitCoordinatingCommitteeKeywords(revisionId, coordinatingCommitteeId) {
    return api.post(
      `/technical-areas/revisions/${revisionId}/keywords/coordinating-committees/${coordinatingCommitteeId}/submit`,
    );
  }

  static getCcKeywordReview(revisionId, coordinatingCommitteeId) {
    return api.get(
      `/technical-areas/revisions/${revisionId}/keywords/coordinating-committees/${coordinatingCommitteeId}/review`,
    );
  }

  static updateCcKeywordReview(revisionId, coordinatingCommitteeId, reviews) {
    return api.put(
      `/technical-areas/revisions/${revisionId}/keywords/coordinating-committees/${coordinatingCommitteeId}/review`,
      { reviews },
    );
  }

  static getTbKeywordReview(revisionId) {
    return api.get(`/technical-areas/revisions/${revisionId}/keywords/review`);
  }

  static updateTbKeywordReview(revisionId, reviews, tbOverrides = {}) {
    const normalizedOverrides = (tbOverrides == null || Array.isArray(tbOverrides))
      ? {}
      : { ...tbOverrides };

    return api.put(`/technical-areas/revisions/${revisionId}/keywords/review`, {
      reviews,
      tbOverrides: normalizedOverrides,
    });
  }

  static baselineSnapshot(revision) {
    if (revision?.baselineSnapshot) {
      return revision.baselineSnapshot;
    }

    return revision?.workflow?.baselineSnapshot || revision?.snapshot || { coordinatingCommittees: [] };
  }

  static baselineKeywordsForTc(baselineSnapshot, technicalCommitteeId) {
    return (baselineSnapshot?.coordinatingCommittees ?? [])
      .flatMap((committee) => committee.technicalCommittees ?? [])
      .find((tc) => Number(tc.id) === Number(technicalCommitteeId))?.keywords ?? [];
  }

  static deriveTcAction(existingKeyword, proposedKeyword) {
    if (!proposedKeyword || proposedKeyword.deleted) {
      return 'remove';
    }

    if (!existingKeyword?.id) {
      return 'add';
    }

    if (Revisions.normalizeKeywordName(existingKeyword.name) === Revisions.normalizeKeywordName(proposedKeyword.name)) {
      return 'keep';
    }

    return 'modify';
  }

  static applyTcActionFromProposedName(row) {
    if (!row?.existingKeywordId) {
      row.tcAction = 'add';
      return row;
    }

    const proposed = String(row.tcProposedName ?? '').trim();

    if (proposed === '') {
      // Keep Modify rather than silently becoming Add for an existing keyword.
      if (row.tcAction !== 'remove') {
        row.tcAction = 'modify';
      }

      return row;
    }

    if (Revisions.normalizeKeywordName(row.existingKeywordName) === Revisions.normalizeKeywordName(proposed)) {
      row.tcAction = 'keep';
    } else {
      row.tcAction = 'modify';
    }

    return row;
  }

  static applyCcActionFromProposedName(row) {
    if (row.ccAction === 'reject') {
      return row;
    }

    if (row.tcAction === 'remove' && row.ccAction === 'accept') {
      return row;
    }

    const proposed = String(row.ccProposedName ?? '').trim();
    const tcProposal = String(row.tcProposedName ?? '').trim();
    const sameName = Revisions.normalizeKeywordName(proposed) === Revisions.normalizeKeywordName(tcProposal);
    const sameTc = Number(row.targetTcId) === Number(row.sourceTcId);

    row.ccAction = (proposed !== '' && sameName && sameTc) ? 'accept' : 'modify';

    return row;
  }

  static tcActionOptionsForRow(row) {
    if (row?.existingKeywordId) {
      return [
        { value: 'keep', text: 'Keep' },
        { value: 'remove', text: 'Remove' },
        { value: 'modify', text: 'Modify' },
      ];
    }

    return [{ value: 'add', text: 'Add' }];
  }

  static buildTcProposalRows(baselineSnapshot, committee, technicalCommittee) {
    const baselineKeywords = Revisions.baselineKeywordsForTc(baselineSnapshot, technicalCommittee.id);
    const proposedKeywords = (technicalCommittee.keywords ?? []).filter((keyword) => !keyword.deleted);
    const tcLabel = committee?.number !== undefined && technicalCommittee?.number !== undefined
      ? `${committee.number}.${technicalCommittee.number}`
      : null;
    const rows = [];
    const proposedById = Object.fromEntries(
      proposedKeywords.filter((keyword) => keyword.id).map((keyword) => [keyword.id, keyword]),
    );

    baselineKeywords.forEach((existingKeyword) => {
      const proposed = proposedById[existingKeyword.id];
      const action = Revisions.deriveTcAction(existingKeyword, proposed);
      rows.push({
        rowId: `existing-${existingKeyword.id}`,
        tcLabel,
        existingKeywordId: existingKeyword.id,
        existingKeywordName: existingKeyword.name,
        tcAction: action,
        tcProposedName: action === 'remove' ? '' : (proposed?.name ?? existingKeyword.name),
        draftId: null,
        keywordRef: proposed || { id: existingKeyword.id, name: existingKeyword.name },
      });
    });

    proposedKeywords
      .filter((keyword) => !keyword.id)
      .forEach((keyword) => {
        rows.push({
          rowId: keyword.draftId || `draft-${rows.length}`,
          tcLabel,
          existingKeywordId: null,
          existingKeywordName: null,
          tcAction: 'add',
          tcProposedName: keyword.name,
          draftId: keyword.draftId,
          keywordRef: keyword,
        });
      });

    return rows;
  }

  static syncKeywordsFromTcRows(rows, technicalCommittee) {
    const keywords = [];

    rows.forEach((row) => {
      // Existing keywords can never become draft/add entries.
      if (row.existingKeywordId && row.tcAction === 'add') {
        Revisions.applyTcActionFromProposedName(row);
      }

      if (row.tcAction === 'remove') {
        if (row.existingKeywordId) {
          keywords.push({
            id: row.existingKeywordId,
            name: row.existingKeywordName,
            deleted: true,
          });
        }

        return;
      }

      const name = String(row.tcProposedName ?? '').trim();

      if (!name) {
        if (row.tcAction === 'add' && !row.existingKeywordId) {
          keywords.push({
            draftId: row.draftId || row.keywordRef?.draftId,
            name: '',
          });
        }

        return;
      }

      if (row.existingKeywordId) {
        keywords.push({ id: row.existingKeywordId, name });
        return;
      }

      keywords.push({
        draftId: row.draftId || row.keywordRef?.draftId,
        name,
      });
    });

    technicalCommittee.keywords = keywords;
  }

  static warningClass(warnings, rowId, column) {
    const warning = warnings?.[column]?.[rowId];

    if (warning === 'exact_duplicate') {
      return 'text-danger font-weight-bold';
    }

    if (warning === 'similar_match') {
      return 'text-warning font-weight-bold';
    }

    return '';
  }

  static ccActionOptions() {
    return [
      { value: 'accept', text: 'Accept' },
      { value: 'reject', text: 'Reject' },
      { value: 'modify', text: 'Modify' },
    ];
  }

  static isCcOriginatedAdd(row) {
    return !row?.existingKeywordId && !String(row?.tcProposedName ?? '').trim();
  }

  static buildCcOriginatedAddRow({ technicalCommittees = [], draftId } = {}) {
    const firstTcId = technicalCommittees[0]?.id ?? null;
    const newDraftId = draftId || (
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`
    );

    return {
      rowId: newDraftId,
      draftId: newDraftId,
      sourceTcId: firstTcId,
      targetTcId: firstTcId,
      existingKeywordId: null,
      existingKeywordName: null,
      tcAction: 'add',
      tcProposedName: null,
      ccAction: 'accept',
      ccProposedName: '',
    };
  }

  static normalizeKeywordName(name) {
    return String(name ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/gu, ' ');
  }

  static findWithinTcDuplicate(keywords, currentKeyword = null) {
    const seen = {};
    const isCurrentKeyword = (keyword) => {
      if (!currentKeyword) {
        return false;
      }

      if (currentKeyword.id && keyword.id === currentKeyword.id) {
        return true;
      }

      return Boolean(currentKeyword.draftId && keyword.draftId === currentKeyword.draftId);
    };

    const duplicate = (keywords ?? []).find((keyword) => {
      if (keyword.deleted || isCurrentKeyword(keyword)) {
        return false;
      }

      const normalized = Revisions.normalizeKeywordName(keyword.name);

      if (normalized === '') {
        return false;
      }

      if (seen[normalized]) {
        return true;
      }

      seen[normalized] = true;

      return false;
    });

    return duplicate?.name ?? null;
  }

  static findEmptyKeywordName(keywords) {
    return (keywords ?? []).some((keyword) => {
      if (keyword.deleted) {
        return false;
      }

      return Revisions.normalizeKeywordName(keyword.name) === '';
    });
  }

  static findWithinTcDuplicatesInSnapshot(snapshot) {
    return (snapshot?.coordinatingCommittees ?? [])
      .filter((committee) => !committee.deleted)
      .flatMap((committee) => (committee.technicalCommittees ?? [])
        .filter((technicalCommittee) => !technicalCommittee.deleted)
        .map((technicalCommittee) => {
          const tcLabel = committee.number !== undefined
            && technicalCommittee.number !== undefined
            ? `${committee.number}.${technicalCommittee.number}`
            : null;
          const duplicateName = Revisions.findWithinTcDuplicate(
            technicalCommittee.keywords ?? [],
          );

          return duplicateName
            ? { tcLabel, name: duplicateName }
            : null;
        }))
      .find(Boolean) ?? null;
  }

  static checkKeywordSimilarity(name, {
    revisionId,
    snapshot,
    excludeKeywordIds = [],
    excludeDraftId,
    mode = 'blocking',
    technicalCommitteeLabel = null,
  } = {}) {
    const payload = {
      name,
      revisionId,
      excludeKeywordIds,
      excludeDraftId,
      mode,
      technicalCommitteeLabel,
    };

    if (snapshot !== undefined) {
      payload.snapshot = snapshot;
    }

    return api.post('/technical-areas/keywords/check-similarity', payload);
  }

  static formatSimilarKeywordsModal(result, technicalCommitteeLabel = null) {
    const exactMatches = result.exactMatches || [];
    const similarMatches = result.similarMatches || [];
    const withinTcDuplicate = Boolean(
      result.isBlocked
      && technicalCommitteeLabel
      && exactMatches.some(
        (match) => match.technicalCommitteeLabel === technicalCommitteeLabel,
      ),
    );

    return {
      exactMatches,
      similarMatches,
      withinTcDuplicate,
    };
  }

  static keywordSimilaritySnack(result, technicalCommitteeLabel = null) {
    if (result.isBlocked) {
      const matchLabel = result.exactMatches?.[0]?.technicalCommitteeLabel;

      if (technicalCommitteeLabel && matchLabel === technicalCommitteeLabel) {
        return {
          variant: 'danger',
          text: 'This keyword already exists in this technical committee.',
        };
      }

      return {
        variant: 'danger',
        text: matchLabel
          ? `This keyword already exists in TC${matchLabel}.`
          : 'This keyword already exists elsewhere.',
      };
    }

    if (result.similarMatches?.length) {
      const labels = [...new Set(
        result.similarMatches
          .map((match) => match.technicalCommitteeLabel)
          .filter(Boolean),
      )];
      const suffix = labels.length
        ? ` in TC${labels.join(', TC')}`
        : ' elsewhere';

      return {
        variant: 'warning',
        text: `A similar keyword exists${suffix}.`,
      };
    }

    return null;
  }

  static withinTcDuplicateSnack(duplicate) {
    if (!duplicate) {
      return null;
    }

    const location = duplicate.tcLabel
      ? `TC${duplicate.tcLabel}`
      : 'this technical committee';

    return {
      variant: 'danger',
      text: `Duplicate keyword "${duplicate.name}" found within ${location}.`,
    };
  }

  static emptyKeywordSnack() {
    return {
      variant: 'danger',
      text: 'Keyword names cannot be empty.',
    };
  }

  static pdfUrl(id, token) {
    return `${process.env.VUE_APP_API_URL}/technical-areas/revisions/${id}/export/pdf?api_token=${token}`;
  }
}

export default Revisions;
